import { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/common/DataTable';
import { useState } from 'react';
import { useDebounce } from '@/hooks';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '@/utils';

import {
  useGetCommunityPartnersQuery,
  useDeleteCommunityPartnerMutation,
} from '@/store/api/partnersApi';

export interface CommunityPartner {
  id: number;
  name: string; // ✅ title → name
  logo: string;
  website?: string | null;
  createdAt: string;
  updatedAt: string;
}

/* ---------------- ACTIONS ---------------- */

function PartnerActions({ partner }: { partner: CommunityPartner }) {
  const [deletePartner, { isLoading }] = useDeleteCommunityPartnerMutation();

  const handleDelete = async () => {
    const confirmDelete = confirm(`Delete "${partner.name}" ?`);
    if (!confirmDelete) return;

    try {
      await deletePartner(partner.id).unwrap();
    } catch (err) {
      console.error('Delete failed', err);
      alert('Failed to delete partner');
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>

        <DropdownMenuItem
          className="text-red-600 focus:text-red-600"
          onClick={handleDelete}
          disabled={isLoading}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ---------------- PAGE ---------------- */

export function CommunityPartners() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const { data, isLoading } = useGetCommunityPartnersQuery({
    search: debouncedSearch,
    skip: pageIndex * pageSize,
    take: pageSize,
  });

  const partners: CommunityPartner[] = data?.data ?? [];
  const totalCount = data?.count ?? 0;

  const columns: ColumnDef<CommunityPartner>[] = [
    {
      header: 'Logo',
      cell: ({ row }) => (
        <div className="flex items-center justify-start">
          <img
            src={row.original.logo}
            alt={row.original.name}
            className="h-10 w-10 object-contain rounded-md border bg-white p-1"
            onError={(e) => ((e.target as HTMLImageElement).src = '/placeholder.png')}
          />
        </div>
      ),
    },
    {
      header: 'Title',
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    },
    {
      header: 'Created At',
      cell: ({ row }) => <span>{formatDate(new Date(row.original.createdAt))}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => <PartnerActions partner={row.original} />,
      enableSorting: false,
    },
  ];

  if (isLoading) return <div className="p-6">Loading partners...</div>;
  // if (isError) return <div className="p-6 text-red-500">Failed to load partners</div>;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">Community Partners</h2>
        <Button onClick={() => navigate('/add-community-partner')}>Add Partner</Button>
      </div>

      {/* Table */}
      <DataTable
        data={partners}
        columns={columns}
        enableFilters={false}
        onSearchChange={(val) => {
          setSearch(val);
          setPageIndex(0); // reset page
        }}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageCount={Math.ceil(totalCount / pageSize)}
        manualPagination
        onPaginationChange={(updater) => {
          const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;

          setPageIndex(next.pageIndex);
          setPageSize(next.pageSize);
        }}
      />
    </div>
  );
}
